import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Card from 'components/insights/layout/Card';
import Page from 'components/layouts/page/Page';
import FilterChips from 'components/shared/FilterChips';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import ControlledTable from 'components/shared/table/ControlledTable';
import { ReferralProvider } from 'context/review/referral';
import useIsMobile from 'hooks/useIsMobile';
import usePagination from 'hooks/usePagination';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import Referral from 'models/reviews/Referral';
import { indexReviews, referralQueryKeys } from 'services/api/reviews';
import { serializeSort } from 'stores/helpers';
import { useReferralsActions, useReferralsStore } from 'stores/referralsStore';
import { FilterOption } from 'stores/types';
import { BodySmall, H3 } from 'styles/typography';
import ReferralsIcon from 'svg/ReferralsIconOutlined';

import ReferralsFilterBar from './ReferralsFilterBar';
import ReferralSlideout from './ReferralSlideout';
import referralsTableColumns from './referralsTableColumns';

function ReferralsListPage() {
  const isMobile = useIsMobile();
  const pagination = usePagination();
  const referralCount = useRef(0);
  const filters = useReferralsStore((state) => state.filters);
  const sort = useReferralsStore((state) => state.sort);
  const search = useReferralsStore((state) => state.search);
  const { removeFilter, setSort, setSearch, setFilters } = useReferralsActions();
  const navigate = useNavigate();

  useEffect(() => () => setSearch(''), [setSearch]);

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
      filters,
      type: 'referral',
      include: 'referred_by,plan_type_classification,patient,episode,group',
    }),
    [search, filters, pagination.paging, sort]
  );

  const { data: reviewsCount } = useQuery({
    queryKey: ['referralsCount'],
    queryFn: ({ signal }) =>
      indexReviews(
        { pageSize: 1, page: 1, filters: { type: new FilterValueArray(['referral' as FilterOption]) } },
        signal
      ),
    select: (data) => data?.meta.totalRecords,
  });

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters]);

  const { data: reviews, isLoading } = useQuery({
    queryKey: referralQueryKeys.index({ ...queryParams }),
    queryFn: ({ signal }) => indexReviews(queryParams, signal),
    select: (data) => {
      referralCount.current = data.meta.totalRecords;
      return data;
    },
  });

  const columns = useMemo(
    () =>
      referralsTableColumns({
        onClickReview: (referralId: string) => navigate(`/referrals/${referralId}`),
        isMobile,
      }),
    [navigate, isMobile]
  );

  const handleRemoveFilter = useCallback(
    (filterKey: string, id: string) => {
      removeFilter(filterKey, id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleApplyFilters = useCallback(
    ({ search, ...filters }) => {
      setFilters(filters);
      setSearch(search);

      if (search) {
        setSort([{ id: 'search', desc: true }]);
      } else if (sort.some(({ id }) => id === 'search')) {
        setSort([{ id: 'createdAt', desc: true }]);
      }
    },
    [setFilters, setSort, setSearch, sort]
  );

  return (
    <>
      <Page>
        <ReferralsFilterBar onApplyFilters={handleApplyFilters} />
        {reviewsCount == 0 ? (
          <StyledCard>
            <IconContainer>
              <ReferralsIcon width={24} height={24} />
            </IconContainer>
            <H3>No Referrals</H3>
            <BodySmall>Any active referrals in Olio will be listed here</BodySmall>
          </StyledCard>
        ) : (
          <>
            <StyledPageSubHeader>
              <DesktopOnly>
                <FilterChips filters={filters} onRemoveFilter={handleRemoveFilter} useStore={useReferralsStore} />
              </DesktopOnly>
              <BodySmall data-cy='resultsCount'>Showing {referralCount.current} results</BodySmall>
            </StyledPageSubHeader>
            <Page.Content>
              <ControlledTable<Referral>
                data={(reviews?.data ?? []) as Referral[]}
                filters={filters}
                columns={columns}
                loading={isLoading}
                pageCount={reviews?.meta.totalPages ?? 1}
                pagination={pagination}
                sorting={{ sortBy: sort, setSortBy: setSort }}
                state={{
                  columnVisibility: {
                    planTypeClassification: !isMobile,
                    referredBy: !isMobile,
                    group: !isMobile,
                    createdAt: !isMobile,
                  },
                }}
              />
            </Page.Content>
          </>
        )}
      </Page>
      <ReferralProvider>
        <ReferralSlideout />
      </ReferralProvider>
    </>
  );
}

export default ReferralsListPage;

const StyledPageSubHeader = styled(Page.SubHeader)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${({ theme }) => theme.devices.desktop} {
    gap: 18px;
  }
`;

const StyledCard = styled(Card)`
  margin: 24px;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
`;

const IconContainer = styled.div`
  color: var(--primary-blue);
`;
